<template>
    <div class="hints-section">
        <h4>Need help? ({{ hintPenalty }} mark penalty per hint)</h4>
        
        <button
            v-if="hints[0] && hintsRevealed < 1"
            @click="$emit('reveal', 1)"
            class="hint-btn"
        >
            Show Hint 1
        </button>

        <div v-if="hintsRevealed >= 1" class="hint">
            <strong>Hint 1:</strong> <span v-html="formatText(hints[0])"></span>
        </div>

        <button
            v-if="hints[1] && hintsRevealed >= 1 && hintsRevealed < 2"
            @click="$emit('reveal', 2)"
            class="hint-btn"
        >
            Show Hint 2
        </button>
        
        <div v-if="hintsRevealed >= 2" class="hint">
            <strong>Hint 2:</strong> <span v-html="formatText(hints[1])"></span>
        </div>

        <button
            v-if="hints[2] && hintsRevealed >= 2 && hintsRevealed < 3"
            @click="$emit('reveal', 3)"
            class="hint-btn"
        >
            Show Hint 3
        </button>
        
        <div v-if="hintsRevealed >= 3" class="hint">
            <strong>Hint 3:</strong> <span v-html="formatText(hints[2])"></span>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUpdated } from 'vue';
import { useMarkdownRenderer } from '@/composables/useMarkdownRenderer';

defineProps({
    hints: {
        type: Array,
        default: () => []
    },
    hintsRevealed: {
        type: Number,
        default: 0
    },
    hintPenalty: {
        type: Number,
        default: 0.5
    }
});

defineEmits(['reveal']);

const { formatText, renderMathAsync } = useMarkdownRenderer();

const renderMath = async () => {
    const hintsSection = document.querySelector('.hints-section');
    await renderMathAsync(hintsSection);
};

onMounted(renderMath);
onUpdated(renderMath);
</script>

<style scoped>
.hints-section {
    margin-bottom: 30px;
    padding: 20px;
    background: var(--bg-secondary);
    border: 2px solid rgba(var(--accent-rgb), 0.4);
}

.hints-section h4 {
    margin: 0 0 15px 0;
    color: var(--text-secondary);
    font-size: 14px;
}

.hint-btn {
    padding: 8px 16px;
    background: var(--bg-primary);
    color: var(--text-primary);
    border: 2px solid rgba(var(--accent-rgb), 0.6);
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
    transition: transform 0.2s, box-shadow 0.2s;
}

.hint-btn:hover {
    transform: translateY(-2px);
}

.hint {
    padding: 12px;
    background: var(--bg-secondary);
    border-left: 4px solid var(--accent);
    margin-top: 10px;
    font-size: 14px;
    color: var(--text-primary);
}

/* Tables from v-html content */
.hint :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
    font-size: 13px;
}

.hint :deep(th),
.hint :deep(td) {
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    padding: 6px 10px;
    text-align: left;
}

.hint :deep(th) {
    background: rgba(var(--accent-rgb), 0.15);
    color: var(--text-secondary);
    font-weight: 600;
}

.hint :deep(tr:nth-child(even)) {
    background: rgba(var(--accent-rgb), 0.05);
}

/* Lists from v-html content */
.hint :deep(ul),
.hint :deep(ol) {
    margin: 8px 0;
    padding-left: 20px;
    text-align: left;
}

.hint :deep(li) {
    margin: 4px 0;
    line-height: 1.4;
}

/* Markdown support */
.hint :deep(h1),
.hint :deep(h2),
.hint :deep(h3),
.hint :deep(h4),
.hint :deep(h5),
.hint :deep(h6) {
    margin: 12px 0 8px 0;
    color: var(--accent);
    font-weight: 600;
}

.hint :deep(h1) { font-size: 18px; }
.hint :deep(h2) { font-size: 16px; }
.hint :deep(h3) { font-size: 15px; }
.hint :deep(h4) { font-size: 14px; }
.hint :deep(h5) { font-size: 13px; }
.hint :deep(h6) { font-size: 13px; }

.hint :deep(p) {
    margin: 8px 0;
    line-height: 1.5;
}

.hint :deep(strong) {
    font-weight: 600;
    color: var(--text-primary);
}

.hint :deep(em) {
    font-style: italic;
}

.hint :deep(code) {
    background: rgba(var(--accent-rgb), 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
}

.hint :deep(pre) {
    background: rgba(var(--accent-rgb), 0.05);
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 10px 0;
}

.hint :deep(pre code) {
    background: none;
    padding: 0;
}

.hint :deep(a) {
    color: var(--accent);
    text-decoration: underline;
}

.hint :deep(a:hover) {
    opacity: 0.8;
}

.hint :deep(blockquote) {
    border-left: 3px solid var(--accent);
    padding-left: 12px;
    margin: 10px 0;
    color: var(--text-secondary);
    font-style: italic;
}

.hint :deep(hr) {
    border: none;
    border-top: 1px solid rgba(var(--accent-rgb), 0.3);
    margin: 12px 0;
}

/* KaTeX math expressions */
.hint :deep(.katex) {
    white-space: nowrap;
}

.hint :deep(.katex-display) {
    white-space: nowrap;
    overflow-x: auto;
}
</style>